import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Check, 
  Code, 
  FileJson, 
  BookOpen,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const APIPage = () => {
  const { token, isAuthenticated } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchApiKeys();
    }
  }, [isAuthenticated]);

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${API_URL}/api/byollm/keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.keys || []);
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/byollm/keys?name=${encodeURIComponent(newKeyName)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewKey(data.api_key);
        setNewKeyName('');
        fetchApiKeys();
        toast.success('API key created! Copy it now - it won\'t be shown again.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create key');
      }
    } catch (err) {
      toast.error('Error creating API key');
    } finally {
      setLoading(false);
    }
  };

  const deleteKey = async (keyPrefix) => {
    if (!confirm('Delete this API key? Any applications using it will stop working.')) return;

    try {
      const response = await fetch(`${API_URL}/api/byollm/keys/${keyPrefix}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('API key deleted');
        fetchApiKeys();
      } else {
        toast.error('Failed to delete key');
      }
    } catch (err) {
      toast.error('Error deleting key');
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-white mb-4">API Access</h1>
          <p className="text-slate-400">Please log in to access the API.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">BYOLLM API</h1>
          <p className="text-slate-400">Bring Your Own LLM - Export portfolio data for AI analysis</p>
        </div>

        {/* API Keys Card */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Key className="h-5 w-5 text-purple-400" />
              API Keys
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create New Key */}
            <div className="flex gap-2">
              <Input
                placeholder="Key name (e.g., 'ChatGPT Integration')"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="bg-slate-800 border-slate-700"
                onKeyPress={(e) => e.key === 'Enter' && createKey()}
              />
              <Button 
                onClick={createKey} 
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-500"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Key
              </Button>
            </div>

            {/* New Key Display */}
            {newKey && (
              <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg">
                <p className="text-green-400 text-sm mb-2">
                  <strong>New API Key Created!</strong> Copy it now - it won't be shown again.
                </p>
                <div className="flex items-center gap-2 bg-slate-800 p-3 rounded font-mono text-sm">
                  <code className="flex-1 text-white break-all">{newKey}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(newKey, 'new')}
                    className="text-green-400 hover:text-green-300"
                  >
                    {copiedKey === 'new' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Keys */}
            <div className="space-y-2">
              {apiKeys.length === 0 ? (
                <p className="text-slate-500 text-sm py-4 text-center">
                  No API keys yet. Create one to get started.
                </p>
              ) : (
                apiKeys.map((key) => (
                  <div 
                    key={key.key_prefix}
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Key className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-white font-medium">{key.name}</p>
                        <p className="text-slate-500 text-xs font-mono">{key.key_prefix}...****</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {key.last_used_at && (
                        <Badge variant="outline" className="text-xs text-slate-400">
                          Used {new Date(key.last_used_at).toLocaleDateString()}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteKey(key.key_prefix)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* API Documentation */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setShowDocs(!showDocs)}
          >
            <CardTitle className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-400" />
                API Documentation
              </div>
              {showDocs ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </CardTitle>
          </CardHeader>
          {showDocs && (
            <CardContent className="space-y-6">
              {/* Authentication */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Authentication</h3>
                <p className="text-slate-400 text-sm mb-3">
                  Include your API key in the request header:
                </p>
                <div className="bg-slate-800 p-3 rounded font-mono text-sm">
                  <code className="text-green-400">X-Api-Key: tt_your_api_key_here</code>
                </div>
              </div>

              {/* Endpoints */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Available Endpoints</h3>
                
                {/* Portfolio */}
                <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code className="text-white">/api/byollm/portfolio</code>
                  </div>
                  <p className="text-slate-400 text-sm mb-2">
                    Get your portfolio data in structured JSON format for LLM analysis.
                  </p>
                  <div className="text-xs text-slate-500">
                    Query params: <code className="text-purple-400">format=json|markdown</code>
                  </div>
                </div>

                {/* Market */}
                <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code className="text-white">/api/byollm/market</code>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Get current market data for all available instruments.
                  </p>
                </div>

                {/* Leaderboard */}
                <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code className="text-white">/api/byollm/leaderboard</code>
                  </div>
                  <p className="text-slate-400 text-sm mb-2">
                    Get leaderboard data for competitive analysis.
                  </p>
                  <div className="text-xs text-slate-500">
                    Query params: <code className="text-purple-400">limit=20</code>
                  </div>
                </div>

                {/* Dividends */}
                <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-600">GET</Badge>
                    <code className="text-white">/api/byollm/dividends</code>
                  </div>
                  <p className="text-slate-400 text-sm mb-2">
                    Get recent and upcoming dividend events.
                  </p>
                  <div className="text-xs text-slate-500">
                    Query params: <code className="text-purple-400">days=30</code>
                  </div>
                </div>
              </div>

              {/* Example */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Example Usage</h3>
                <div className="bg-slate-800 p-4 rounded font-mono text-sm overflow-x-auto">
                  <pre className="text-slate-300">{`curl -X GET "${API_URL}/api/byollm/portfolio" \\
  -H "X-Api-Key: tt_your_api_key_here"`}</pre>
                </div>
              </div>

              {/* LLM Tips */}
              <div className="p-4 bg-purple-900/20 border border-purple-700/50 rounded-lg">
                <h3 className="text-purple-300 font-semibold mb-2">Tips for LLM Integration</h3>
                <ul className="text-slate-300 text-sm space-y-1">
                  <li>• Use <code className="text-purple-400">format=markdown</code> for ChatGPT/Claude</li>
                  <li>• Include the scoring rules in your prompt for better recommendations</li>
                  <li>• Ask the LLM to optimize for points, not just returns</li>
                  <li>• Combine portfolio + market data for comprehensive analysis</li>
                </ul>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default APIPage;
